import React, { useEffect, useRef, useState } from 'react'
import styled, { css } from 'styled-components'
import { connect } from 'react-redux'
import { mapLanguageToComponentProps } from 'i18n'
import { I18nComponentType } from 'i18n-types'
import ReactDOM from 'react-dom'

import { ButtonBox } from 'components/styled/button/base-button'
import { StyledButton } from 'components/styled/button/button'
import { Body, Subtitle } from '../typography'
import { getActionBarRoot as getActionBarRootDefault } from '../page/app-content'
import { Button } from '../button'
import { MobileRatingButton } from '../rating/mobile'
import { BaseMenuItem } from '../menu/menu-item/base-menu-button'
import { PopoverMenu } from '../popover/menu'
import { MenuSection } from '../menu/menu-section'
import { Flex } from '../containers/flex'

export interface ActionBarProps {
  actions?: React.ReactNode
  hiddenActions?: React.ReactNode
  useMobileMenu?: boolean
  dispatch?: (...args: unknown[]) => unknown
  infoText?: string
  selection?: {
    count: number
    text: string
  }
  leftSideComponent?: React.ReactNode
  getActionBarRoot?: () => HTMLElement | null
  hideActionBar?: boolean
  navigationActionProps?: {
    hasNext: boolean
    hasPrevious: boolean
    onNext: () => void
    onPrev: () => void
    total: number
    disableArrowKeyNavigation: boolean
  }
}

const ActionBarWrapper = styled.div<{ $setHidden?: boolean }>`
  background-color: ${({ theme }) => theme.color.background};

  ${({ theme }) => theme.mediaQuery.phone} {
    display: ${({ $setHidden }) => ($setHidden ? 'none' : 'block')};
  }
`

const StyledActionBar = styled.div.withConfig({
  shouldForwardProp: (prop) => !['language'].includes(prop),
})`
  box-shadow: ${({ theme }) => theme.shadow.top};
  box-sizing: border-box;
  min-height: ${({ theme }) => theme.space.s64};
`

const LeftSide = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.space.s16};
  align-items: center;

  ${({ theme }) => theme.mediaQuery.screenM} {
    flex-direction: column-reverse;
    gap: ${({ theme }) => theme.space.s20};
    justify-content: space-between;
    align-items: flex-start;
  }

  ${({ theme }) => theme.mediaQuery.tablet} {
    flex-direction: column-reverse;
  }

  ${({ theme }) => theme.mediaQuery.phone} {
    flex-direction: column;
  }
`

const RightSide = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.space.s16};
  align-items: center;

  ${({ theme }) => theme.mediaQuery.screenM} {
    flex-direction: column;
    gap: ${({ theme }) => theme.space.s20};
    justify-content: space-between;
    align-items: flex-end;
  }

  ${({ theme }) => theme.mediaQuery.tablet} {
    flex-direction: column;
  }
`

const ActionBarContentWrapper = styled.div<{
  $useMobileMenu: boolean
  $hiddenActions?: React.ReactNode
  $showOnlyMobile?: boolean
}>`
  display: ${({ $showOnlyMobile }) => ($showOnlyMobile ? 'none' : 'flex')};
  justify-content: space-between;
  align-items: center;

  background-color: ${({ theme }) => theme.color.actionBarBackground};
  padding: ${({ theme }) => theme.space.s12} ${({ theme }) => theme.space.s48};

  ${({ theme }) => theme.mediaQuery.screenS} {
    gap: ${({ theme }) => theme.space.s8};
    align-items: stretch;
    padding-left: ${({ theme }) => theme.space.s16};
    padding-right: ${({ theme }) => theme.space.s16};
    padding-top: ${({ theme, $showOnlyMobile }) =>
      $showOnlyMobile ? theme.space.s0 : theme.space.s20};
  }

  ${({ theme }) => theme.mediaQuery.tablet} {
    align-items: center;

    ${({ $hiddenActions }) =>
      $hiddenActions &&
      css`
        flex-direction: row;
        align-items: stretch;
      `}
  }

  ${({ theme }) => theme.mediaQuery.phone} {
    gap: ${({ theme }) => theme.space.s8};
    display: flex;

    ${({ $useMobileMenu, $hiddenActions }) =>
      !$useMobileMenu &&
      !$hiddenActions &&
      css`
        flex-direction: column;
        align-items: stretch;
      `}

    ${({ $useMobileMenu, $hiddenActions }) =>
      ($useMobileMenu || $hiddenActions) &&
      css`
        flex-direction: column-reverse;
        align-items: stretch;

        ${LeftSide} {
          padding-left: 0;
        }

        ${BaseMenuItem} {
          width: 100% !important;
        }

        ${ButtonBox} {
          width: 100%;

          ${StyledButton} {
            width: 100%;
          }

          ${MobileRatingButton} {
            width: 100%;
          }
        }
      `}
  }
`

const LeftSideText = styled(LeftSide)`
  display: block;
  white-space: nowrap;
  ${({ theme }) => theme.mediaQuery.tablet} {
    white-space: break-spaces;
  }
`

const SelectionNumber = styled(Subtitle).attrs({
  fontWeight: 600,
})`
  display: inline;
  margin-bottom: 0;
  margin-right: ${({ theme }) => theme.space.s4};
`

const SelectionText = styled(Subtitle)`
  display: inline;
  margin-bottom: 0;
`

const Actions = styled.div<{ $hiddenActions?: React.ReactNode }>`
  display: flex;
  justify-content: flex-end;
  flex-wrap: wrap;
  align-items: center;
  flex-grow: 1;
  gap: ${({ theme }) => theme.space.s8};

  ${({ theme }) => theme.mediaQuery.tablet} {
    width: 100%;
  }
`

const PrimaryActions = styled(Actions)<{
  $useMobileMenu: boolean
  $hiddenActions?: React.ReactNode
}>`
  ${({ theme }) => theme.mediaQuery.tablet} {
    ${({ $useMobileMenu }) =>
      $useMobileMenu &&
      css`
        display: none;
      `}
  }

  ${({ theme }) => theme.mediaQuery.phone} {
    flex-direction: column-reverse;

    ${ButtonBox} {
      width: 100%;

      ${StyledButton} {
        width: 100%;
      }
    }
  }
`

const StyledNavButton = styled(Button)<{
  $hidden?: boolean
  $showOnlyOnMobile?: boolean
}>`
  display: ${({ $showOnlyOnMobile }) =>
    $showOnlyOnMobile ? 'none' : 'inline-flex'};
  visibility: ${({ $hidden }) => ($hidden ? 'hidden' : 'visible')};

  ${({ theme }) => theme.mediaQuery.tablet} {
    button span {
      display: none !important;
    }
  }

  ${({ theme }) => theme.mediaQuery.phone} {
    display: ${({ $showOnlyOnMobile }) =>
      $showOnlyOnMobile ? 'block' : 'none'};
  }
`

const StyledPopoverMenu = styled(PopoverMenu)<{ $mobileMenu?: boolean }>`
  display: ${({ $mobileMenu }) => ($mobileMenu ? 'none' : 'block')};

  ${({ theme }) => theme.mediaQuery.tablet} {
    display: block;
  }
`

const ActionBarFC = ({
  i18n,
  actions,
  useMobileMenu = false,
  selection,
  infoText,
  leftSideComponent,
  hideActionBar = false,
  hiddenActions,
  navigationActionProps,
  getActionBarRoot = getActionBarRootDefault,
  dispatch: _dispatch,
  ...props
}: ActionBarProps & { i18n: I18nComponentType }) => {
  const [isPopoverMenuOpen, setPopoverMenuOpen] = useState(false)
  const parentRef = useRef(document.createElement('div'))

  useEffect(() => {
    const actionBarRoot = getActionBarRoot && getActionBarRoot()
    actionBarRoot?.appendChild(parentRef.current)
    return () => {
      actionBarRoot?.removeChild(parentRef.current)
    }
  }, [])

  const onKeyDown = (event: KeyboardEvent) => {
    if (
      event.key === 'ArrowLeft' &&
      !navigationActionProps?.disableArrowKeyNavigation &&
      navigationActionProps?.hasPrevious &&
      navigationActionProps?.onPrev
    ) {
      navigationActionProps.onPrev()
    }
    if (
      event.key === 'ArrowRight' &&
      !navigationActionProps?.disableArrowKeyNavigation &&
      navigationActionProps?.hasNext &&
      navigationActionProps?.onNext
    ) {
      navigationActionProps.onNext()
    }
  }

  useEffect(() => {
    window.addEventListener('keydown', onKeyDown)
    return () => {
      window.removeEventListener('keydown', onKeyDown)
    }
  }, [navigationActionProps])

  const allActions = (
    <>
      {actions}
      {hiddenActions}
    </>
  )

  const popoverMenu = (actions: React.ReactNode, mobileMenu?: boolean) => {
    return (
      <StyledPopoverMenu
        $mobileMenu={mobileMenu}
        maxTriggerWidth="s160"
        customIsMenuOpen={isPopoverMenuOpen}
        customSetMenuOpen={setPopoverMenuOpen}
        triggerButtonProps={{
          variant: 'primaryGhost',
          iconRight: 'keyboard_arrow_up',
          iconRightClose: 'keyboard_arrow_down',
          text: isPopoverMenuOpen
            ? i18n.actionBar.hideActions
            : i18n.actionBar.actions,
          textVariant: 'subtitle',
          buttonStyle: 'rounded',
        }}
      >
        <MenuSection>{actions}</MenuSection>
      </StyledPopoverMenu>
    )
  }

  const component = (
    <ActionBarWrapper $setHidden={hideActionBar}>
      <StyledActionBar {...props}>
        <ActionBarContentWrapper
          $useMobileMenu={useMobileMenu}
          $hiddenActions={hiddenActions}
        >
          <LeftSide>
            {selection && (
              <Flex flexDirection="column" gap="s4">
                <LeftSideText>
                  <SelectionNumber>{selection.count}</SelectionNumber>
                  <SelectionText>{selection.text}</SelectionText>
                </LeftSideText>
                {infoText && <Body>{infoText}</Body>}
              </Flex>
            )}
            {navigationActionProps &&
              navigationActionProps.onPrev &&
              navigationActionProps.total > 1 && (
                <StyledNavButton
                  icon="chevron_left"
                  variant="secondary"
                  text={i18n.actionBar.previous}
                  onClick={navigationActionProps.onPrev}
                  $hidden={!navigationActionProps.hasPrevious}
                />
              )}
            {leftSideComponent}
          </LeftSide>
          <RightSide>
            <PrimaryActions $useMobileMenu={useMobileMenu}>
              {actions}
              {hiddenActions && popoverMenu(hiddenActions)}
            </PrimaryActions>
            {useMobileMenu && popoverMenu(allActions, true)}
            {navigationActionProps &&
              navigationActionProps.total > 1 &&
              navigationActionProps.onNext && (
                <StyledNavButton
                  icon="chevron_right"
                  variant="secondary"
                  text={i18n.actionBar.next}
                  $hidden={!navigationActionProps.hasNext}
                  onClick={navigationActionProps.onNext}
                />
              )}
          </RightSide>
        </ActionBarContentWrapper>
        <ActionBarContentWrapper
          $useMobileMenu={useMobileMenu}
          $hiddenActions={hiddenActions}
          $showOnlyMobile
        >
          {navigationActionProps &&
            navigationActionProps.onPrev &&
            navigationActionProps.total > 1 && (
              <StyledNavButton
                icon="chevron_left"
                variant="secondary"
                text="Previous"
                onClick={navigationActionProps.onPrev}
                $hidden={!navigationActionProps.hasPrevious}
                $showOnlyOnMobile
              />
            )}
          {navigationActionProps &&
            navigationActionProps.total > 1 &&
            navigationActionProps.onNext && (
              <StyledNavButton
                icon="chevron_right"
                variant="secondary"
                text="Next"
                $hidden={!navigationActionProps.hasNext}
                onClick={navigationActionProps.onNext}
                $showOnlyOnMobile
              />
            )}
        </ActionBarContentWrapper>
      </StyledActionBar>
    </ActionBarWrapper>
  )

  return parentRef.current
    ? ReactDOM.createPortal(component, parentRef.current)
    : null
}

export const ActionBar = connect<{ i18n: I18nComponentType }>(
  mapLanguageToComponentProps()
)(ActionBarFC)
